import { Suspense } from 'react'
import { Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { ProjectManagementProjectDetailsPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const ProjectDetailsPage = () => {
  const location = useLocation()

  const { company } = useCurrentSession()
  const { hasBudgetFeature, hasCostsFeature, hasExpenses, hasPayingProjectsFeature } =
    useOutletContext<{
      hasBudgetFeature: boolean
      hasCostsFeature: boolean
      hasExpenses: boolean
      hasPayingProjectsFeature: boolean
    }>()

  useEffect(() => {
    track(trackingEvents.projectManagement.projects.goToProjectDetails)
  }, [])

  const { id } = useParams()

  const { data, loading, error } = useQuery(ProjectManagementProjectDetailsPageDocument, {
    variables: {
      companyId: company.id,
      featureIds: [
        Features.projects_clients,
        Features.projects_overview,
        Features.projects_tasks,
        Features.projects_flexible_tracking,
      ],
      hasSpendings: hasCostsFeature && hasExpenses,
      hasBudgetFeature,
      projectId: String(id),
    },
  })

  const project = data ? data.projectManagement.projectsConnection.nodes[0] : null

  const ratesBreakdowns = data?.projectManagement.ratesBreakdownsConnection.nodes || []

  const contextlessPolicies = data ? data.projectManagement._policies : null
  const attendanceContextlessPolicies = data ? data.attendance._policies : null

  const hasProjectTasks = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_tasks
  )?.available
  const hasOverviewEnabled = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_overview
  )?.available
  const hasFlexibleTracking = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_flexible_tracking
  )?.available

  const hasSubprojectsEntityFeature = !!data?.features.featuresConnection.nodes.find(
    (feature) => feature.name === 'dev_projects_subprojects_entity'
  )?.enabled

  const hasClientsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_clients
  )?.available

  const isSaleInvoiceDetail = location.pathname.includes('/sale_invoices/detail/')

  const taskTotalCount = data?.projectManagement.totalTasks.totalCount || 0
  const completedTasksCount = data?.projectManagement.completedTasks.totalCount || 0
  const overdueTasksCount = data?.projectManagement.overdueTasks.totalCount || 0
  const spendingCostsApprovedAmountCents =
    data?.projectManagement.spendingCostsInsightsConnection.edges[0]?.node.approvedAmountCents || 0
  const spendingCostsPendingAmountCents =
    data?.projectManagement.spendingCostsInsightsConnection.edges[0]?.node.pendingAmountCents || 0

  return (
    <ResponsivePage.Body
      variant={isSaleInvoiceDetail ? 'fullscreen' : 'wide'}
      loading={loading}
      error={error}
      authorized={!!project}
    >
      <Suspense>
        <Outlet
          context={{
            attendanceContextlessPolicies,
            completedTasksCount,
            contextlessPolicies,
            project,
            hasBudgetFeature,
            hasClientsFeature,
            hasCost: hasCostsFeature,
            hasExpenses,
            hasFlexibleTracking,
            hasPayingProjectsFeature,
            hasProjectTasks,
            hasPendingSpendings: spendingCostsPendingAmountCents > 0,
            hasOverviewEnabled,
            hasSubprojectsEntityFeature,
            hasSubprojectsFeature: hasSubprojectsEntityFeature,
            overdueTasksCount,
            ratesBreakdowns,
            spendingCostsApprovedAmountCents,
            taskTotalCount,
          }}
        />
      </Suspense>
    </ResponsivePage.Body>
  )
}

export default ProjectDetailsPage
